import { FC } from "react";
import { useTranslation } from "react-i18next";

interface DateRangePresetsProps {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  className?: string;
}

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

// Returns the [start, end] range (YYYY-MM-DD, local time) for a preset key.
export const getPresetRange = (key: string): [string, string] => {
  const now = new Date();
  let start = new Date(now);
  let end = new Date(now);
  switch (key) {
    case "today":
      break;
    case "last7":
      start.setDate(now.getDate() - 6);
      break;
    case "last30":
      start.setDate(now.getDate() - 29);
      break;
    case "thisMonth":
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case "lastMonth":
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      end = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
      break;
    case "thisYear":
      start = new Date(now.getFullYear(), 0, 1);
      break;
  }
  return [toYmd(start), toYmd(end)];
};

const PRESET_KEYS = [
  "today",
  "last7",
  "last30",
  "thisMonth",
  "lastMonth",
  "thisYear",
] as const;

/**
 * A row of quick date-range chips. The active chip is derived from the current
 * startDate/endDate, so manually editing the dates clears the highlight
 * automatically and no extra state is needed in the parent.
 */
const DateRangePresets: FC<DateRangePresetsProps> = ({
  startDate,
  endDate,
  onChange,
  className,
}) => {
  const { t } = useTranslation(["reports"]);

  const activeKey = PRESET_KEYS.find((key) => {
    const [s, e] = getPresetRange(key);
    return s === startDate && e === endDate;
  });

  const labels: Record<string, string> = {
    today: t("presetToday"),
    last7: t("presetLast7"),
    last30: t("presetLast30"),
    thisMonth: t("presetThisMonth"),
    lastMonth: t("presetLastMonth"),
    thisYear: t("presetThisYear"),
  };

  return (
    <div className={"flex flex-wrap gap-2 " + (className ?? "")}>
      {PRESET_KEYS.map((key) => {
        const isActive = key === activeKey;
        return (
          <button
            key={key}
            type="button"
            onClick={() => {
              const [s, e] = getPresetRange(key);
              onChange(s, e);
            }}
            className={
              "rounded-full border px-3 py-1 text-sm font-medium transition-colors " +
              (isActive
                ? "border-blue-500 bg-blue-500 text-white"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700")
            }
          >
            {labels[key]}
          </button>
        );
      })}
    </div>
  );
};

export default DateRangePresets;
